/**
 * Authentication State Manager
 *
 * Provides utilities for preserving and restoring application state during
 * authentication redirects, ensuring a seamless user experience.
 */

interface PreAuthState {
  pathname: string;
  search: string;
  hash: string;
  timestamp: number;
  tenantState?: unknown;
  userPreferences?: unknown;
}

const AUTH_STATE_KEY = 'mos_pre_auth_state';
const AUTH_STATE_PARAM_KEY = 'mos_auth_state';

/**
 * Store current application state before authentication redirect
 *
 * Captures the current URL state and timestamp to restore user context
 * after authentication completes.
 */
export const storePreAuthState = (): void => {
  try {
    const preAuthState: PreAuthState = {
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      timestamp: Date.now(),
      // Add any additional state that needs preservation
    };

    sessionStorage.setItem(AUTH_STATE_KEY, JSON.stringify(preAuthState));
    // Only log pre-auth state storage in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('💾 Stored pre-auth state:', preAuthState);
    }
  } catch (error) {
    console.warn('Failed to store pre-auth state:', error);
  }
};

/**
 * Restore application state after authentication redirect return
 *
 * Retrieves and validates stored state, ensuring it hasn't expired.
 *
 * @returns The stored pre-auth state or null if not found/expired
 */
export const restorePreAuthState = (): PreAuthState | null => {
  try {
    const storedState = sessionStorage.getItem(AUTH_STATE_KEY);
    if (!storedState) {
      return null;
    }

    const preAuthState: PreAuthState = JSON.parse(storedState);

    // Check if state is not too old (max 10 minutes)
    const maxAge = 10 * 60 * 1000; // 10 minutes
    if (Date.now() - preAuthState.timestamp > maxAge) {
      console.warn('Pre-auth state expired, ignoring');
      sessionStorage.removeItem(AUTH_STATE_KEY);
      return null;
    }

    // Clean up stored state
    sessionStorage.removeItem(AUTH_STATE_KEY);

    console.log('📂 Restored pre-auth state:', preAuthState);
    return preAuthState;
  } catch (error) {
    console.warn('Failed to restore pre-auth state:', error);
    return null;
  }
};

/**
 * Generate secure state parameter for OAuth flow
 *
 * Creates a cryptographically secure random state parameter to prevent
 * CSRF attacks during the OAuth flow.
 *
 * @returns A secure random state string
 */
export const generateAuthState = (): string => {
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);
  const state = Array.from(randomBytes, (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');

  // Store state for validation
  sessionStorage.setItem(AUTH_STATE_PARAM_KEY, state);

  return state;
};

/**
 * Validate returned state parameter
 *
 * Compares the returned state parameter with the stored one to ensure
 * the authentication response is legitimate and not a CSRF attack.
 *
 * @param returnedState - The state parameter returned from the auth provider
 * @returns True if the state is valid, false otherwise
 */
export const validateAuthState = (returnedState: string): boolean => {
  try {
    const storedState = sessionStorage.getItem(AUTH_STATE_PARAM_KEY);

    if (!storedState) {
      console.warn('No stored state found for validation');
      return false;
    }

    // Clean up stored state
    sessionStorage.removeItem(AUTH_STATE_PARAM_KEY);

    const isValid = storedState === returnedState;

    if (!isValid) {
      console.error('State parameter mismatch - possible CSRF attack');
    }

    return isValid;
  } catch (error) {
    console.error('State validation failed:', error);
    return false;
  }
};

/**
 * Clear all stored authentication state
 *
 * Utility function to clean up any stored authentication state,
 * useful for logout or error scenarios.
 */
export const clearAuthState = (): void => {
  try {
    sessionStorage.removeItem(AUTH_STATE_KEY);
    sessionStorage.removeItem(AUTH_STATE_PARAM_KEY);
    // Only log auth state clearing in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('🧹 Cleared all auth state');
    }
  } catch (error) {
    console.warn('Failed to clear auth state:', error);
  }
};

/**
 * Check if we're currently handling a redirect return
 *
 * Determines if the current page load is the result of an authentication
 * redirect by checking for common OAuth parameters in the URL.
 * This is a more conservative check that reduces false positives.
 *
 * @returns True if this appears to be a redirect return
 */
export const isRedirectReturn = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash;

  // Check for OAuth authorization code flow parameters (successful auth)
  const hasAuthCode = urlParams.has('code') && urlParams.has('state');

  // Check for OAuth implicit flow parameters (less common but still valid)
  const hasImplicitFlow =
    hash.includes('access_token') || hash.includes('id_token');

  // Check for OAuth error responses (but don't treat as redirect to handle)
  const hasOAuthError = urlParams.has('error') || hash.includes('error');

  // Check for MSAL-specific parameters
  const hasMsalState =
    urlParams.has('state') &&
    (urlParams.get('state')?.includes('msal') ?? false);

  // Only consider it a redirect if we have clear OAuth SUCCESS evidence
  // Don't treat OAuth errors as redirects to handle - let them show login page
  const isValidRedirectPath = !window.location.pathname.startsWith('/admin');

  const isRedirect =
    (hasAuthCode || hasImplicitFlow || hasMsalState) &&
    isValidRedirectPath &&
    !hasOAuthError; // Don't handle OAuth errors as redirects

  // Add development logging for debugging redirect flow issues
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Redirect detection analysis:', {
      hasAuthCode,
      hasImplicitFlow,
      hasOAuthError,
      hasMsalState,
      isValidRedirectPath,
      isRedirect,
      url: window.location.href,
      search: window.location.search,
      hash: window.location.hash,
    });
  }

  return isRedirect;
};
